import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { AppShell } from '../design-system/shell/app-shell';
import { NavIdentity, type NavEntry } from '../design-system/shell/nav';
import { PageHeader } from '../design-system/shell/page-header';
import { ActionButton } from '../design-system/concept/action-button';
import { CoachCard } from '../design-system/concept/coach-card';
import { Field, ChoiceField } from '../design-system/concept/field';
import { ClientsIcon, SettingsIcon } from '../design-system/shell/icons';
import { useToast } from '../design-system/primitives/use-toast';

import { addProfile, AuthProvider } from '../lib/http-service';
import { useAuth } from '../context/AuthContext';
import { ageFromBirthDate, isCreatableAge, MIN_AGE, MAX_AGE } from '../lib/age';
import { weightFieldProblem } from '../lib/scan/weight';
import { hasFeature, noun } from '../lib/features';

// Retail measures for fit and neither asks for nor shows a weight.
const WEIGHT = hasFeature('weight');

const NAV: NavEntry[] = [
  { key: 'clients', label: noun.Clients, Icon: ClientsIcon },
  { key: 'settings', label: 'Settings', Icon: SettingsIcon, footer: true, desktopOnly: true },
];

type Form = {
  firstName: string;
  lastName: string;
  email: string;
  birthDate: string;
  height: string;
  weight: string;
  gender: 'male' | 'female';
};

const EMPTY: Form = {
  firstName: '',
  lastName: '',
  email: '',
  // Empty, not today's date. Pre-filling with now made the field look answered
  // while computing an age of 0, so the first submit always failed on a value
  // nobody had entered.
  birthDate: '',
  height: '',
  weight: '',
  gender: 'male',
};

const CreateClient = () => {
  const history = useHistory();
  const { toast } = useToast();
  const { getAccessTokenSilently, user } = useAuth();
  const [auth] = useState(() => new AuthProvider(getAccessTokenSilently));

  const [form, setForm] = useState<Form>(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (field: keyof Form) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /**
   * Per-field, so the reader is told which box is wrong rather than being
   * handed "Please fill in all required fields" and left to hunt.
   */
  const errors = useMemo(() => {
    const e: Partial<Record<keyof Form, string>> = {};
    if (!form.firstName.trim()) e.firstName = 'Required.';
    if (!form.lastName.trim()) e.lastName = 'Required.';
    if (!form.email.trim()) e.email = 'Required.';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()))
      e.email = 'That does not look like an email address.';

    if (!form.birthDate) {
      e.birthDate = 'Required.';
    } else {
      const age = ageFromBirthDate(new Date(form.birthDate));
      if (!isCreatableAge(age)) {
        e.birthDate =
          age < MIN_AGE
            ? `${noun.Clients} must be ${MIN_AGE} or older. That date gives ${age}.`
            : `That date gives an age of ${age}, over the ${MAX_AGE} limit.`;
      }
    }

    const height = Number(form.height);
    if (!form.height) e.height = 'Required.';
    else if (!Number.isFinite(height) || height < 100 || height > 250)
      e.height = 'Between 100 and 250 cm.';

    const weightProblem = WEIGHT ? weightFieldProblem(form.weight) : null;
    if (weightProblem) e.weight = weightProblem;
    return e;
  }, [form]);

  const valid = Object.keys(errors).length === 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!valid) return;

    setSaving(true);
    try {
      await addProfile(
        {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          gender: form.gender,
          birthDate: new Date(form.birthDate),
          // Numbers, not the strings the inputs hand back. ClientData types
          // both as numbers, and posting a string put a quoted height in the
          // profile that every later calculation had to coerce.
          height: Number(form.height),
          ...(WEIGHT && form.weight ? { weight: Number(form.weight.replace(',', '.')) } : {}),
          age: ageFromBirthDate(new Date(form.birthDate)),
          account: '',
          ID: '',
          createdAt: new Date(),
        },
        auth,
      );
      toast({ title: `${noun.Client} created`, description: `${form.firstName} is ready to scan.` });
      history.push('/clients');
    } catch (err) {
      console.error('Error creating client:', err);
      toast({
        title: `Could not create the ${noun.client}`,
        description: 'The request did not go through. Try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Errors appear once, on submit, rather than scolding an empty form.
  const show = (field: keyof Form) => (submitted ? errors[field] : undefined);

  return (
    <AppShell
      entries={NAV}
      active="clients"
      onSelect={() => history.push('/clients')}
      onHome={() => history.push('/overview')}
      hidePhoneTabs
      phoneTitle={`New ${noun.client}`}
      phoneBack={() => history.push('/clients')}
      identity={<NavIdentity name={user?.name ?? 'Coach'} detail="Coach" imageUrl={user?.picture} />}
    >
      <div className="mx-auto flex max-w-[720px] flex-col gap-3.5 p-4 lg:p-7">
        <PageHeader
          eyebrow="Roster"
          title={`New ${noun.client}`}
          meta="Name, age and height are what the scan pipeline needs."
          actions={
            <ActionButton onClick={() => history.push('/clients')}>
              Back to {noun.clients}
            </ActionButton>
          }
        />

        <form onSubmit={submit} noValidate>
          <CoachCard
            testId="create-client"
            provenance={`Height is used to scale every measurement taken from the mesh, so it has to be the ${noun.client}'s real height.`}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id="firstName"
                label="First name"
                required
                value={form.firstName}
                onChange={set('firstName')}
                error={show('firstName')}
              />
              <Field
                id="lastName"
                label="Last name"
                required
                value={form.lastName}
                onChange={set('lastName')}
                error={show('lastName')}
              />
            </div>

            <Field
              id="email"
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={set('email')}
              error={show('email')}
            />

            <div className={`grid gap-4 ${WEIGHT ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
              <Field
                id="birthDate"
                label="Birth date"
                type="date"
                required
                value={form.birthDate}
                onChange={set('birthDate')}
                error={show('birthDate')}
                hint={
                  form.birthDate && !errors.birthDate
                    ? `${ageFromBirthDate(new Date(form.birthDate))} years old`
                    : undefined
                }
              />
              <Field
                id="height"
                label="Height (cm)"
                type="number"
                required
                min={100}
                max={250}
                value={form.height}
                onChange={set('height')}
                error={show('height')}
              />
              {WEIGHT && (
                <Field
                  id="weight"
                  label="Weight (kg)"
                  type="number"
                  min={1}
                  max={500}
                  value={form.weight}
                  onChange={set('weight')}
                  error={show('weight')}
                  hint={!form.weight ? 'Optional' : undefined}
                />
              )}
            </div>

            <ChoiceField
              label="Gender"
              required
              value={form.gender}
              options={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
              ]}
              onChange={(v) => set('gender')(v)}
            />

            <div className="mt-1 flex gap-2">
              {/* type=submit rather than an onClick, so Enter in any field
                  sends the form through the same validation path. */}
              <ActionButton
                type="submit"
                variant="primary"
                disabled={saving}
                testId="create-client-submit"
              >
                {saving ? 'Creating…' : `Create ${noun.client}`}
              </ActionButton>
              <ActionButton onClick={() => history.push('/clients')} disabled={saving}>
                Cancel
              </ActionButton>
            </div>
          </CoachCard>
        </form>
      </div>
    </AppShell>
  );
};

export default CreateClient;
